import { useRef, useCallback } from 'react';

/**
 * Custom hook for hamburger menu handling with robust double-trigger prevention
 * @param {Function} onToggle - Function to call when menu should toggle
 * @param {Object} options - Configuration options
 * @returns {Function} - The click handler function
 */
export const useHamburgerMenu = (onToggle, options = {}) => {
  const { 
    debounceDelay = 600, // Increased delay for more aggressive protection
    logClicks = false 
  } = options;
  
  const isProcessingRef = useRef(false);
  const lastClickTimeRef = useRef(0);
  const clickCountRef = useRef(0);

  const handleClick = useCallback((event) => {
    const currentTime = Date.now();
    const timeSinceLastClick = currentTime - lastClickTimeRef.current;
    
    // Increment click count for debugging
    clickCountRef.current += 1;
    
    if (logClicks) {
    }

    // Multiple layers of protection against double triggers
    if (isProcessingRef.current) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return;
    }

    if (timeSinceLastClick < debounceDelay) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return;
    }

    // Set processing flag immediately
    isProcessingRef.current = true;
    lastClickTimeRef.current = currentTime;

    // Prevent default behaviors
    event?.preventDefault?.();
    event?.stopPropagation?.();

    // Execute the toggle function
    try {
      onToggle();
    } catch (error) {
      console.error('Error in hamburger menu toggle:', error);
    }

    // Reset processing flag after delay
    setTimeout(() => {
      isProcessingRef.current = false;
    }, debounceDelay);

  }, [onToggle, debounceDelay, logClicks]);

  return handleClick;
};

/**
 * Enhanced version with additional touch event handling
 */
export const useHamburgerMenuWithTouch = (onToggle, options = {}) => {
  const baseHandler = useHamburgerMenu(onToggle, options);
  const touchStartTimeRef = useRef(0);

  const handleTouchStart = useCallback((event) => {
    touchStartTimeRef.current = Date.now();
  }, []);

  const handleTouchEnd = useCallback((event) => {
    const touchDuration = Date.now() - touchStartTimeRef.current;
    
    // Only treat as click if it's a quick tap (not a long press or swipe)
    if (touchDuration < 300) {
      baseHandler(event);
    }
  }, [baseHandler]);

  const handleClick = useCallback((event) => {
    // For mouse clicks, use the base handler directly
    if (event.type === 'click' && !('ontouchstart' in window)) {
      baseHandler(event);
    }
  }, [baseHandler]);

  return {
    onClick: handleClick,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd
  };
};